import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { UseNotificationsResult } from '../../Hooks/useNotifications';
import { getActiveBrandConfig } from '../../config/environment.config';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import NotificationList from './NotificationList';

interface NotificationPanelProps {
  id?: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** État partagé fourni par la cloche (useNotifications). */
  notifications: UseNotificationsResult;
}

/**
 * Panneau de la cloche : en-tête avec le nombre de non lues et « Tout marquer comme lu »,
 * puces de filtre, liste groupée par ancienneté (NotificationList) et lien vers la page
 * complète des notifications.
 */
const NotificationPanel: React.FC<NotificationPanelProps> = ({ id, anchorEl, onClose, notifications }) => {
  const { brandConfig } = useTailwindBrand();
  const navigate = useNavigate();
  const { items, unreadCount, refresh, markAllAsRead } = notifications;
  const open = Boolean(anchorEl);
  const primary = brandConfig.colors.primary;
  const hasUnread = unreadCount > 0 || items.some((item) => !item.isRead);

  // ouverture : recharge la liste si elle est absente ou périmée
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleSeeAll = () => {
    onClose();
    navigate(`/${getActiveBrandConfig()}/tableau-de-bord/notifications`);
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      marginThreshold={8}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 1.5,
            // presque plein écran sur mobile, 380 px à partir de la tablette
            width: { xs: 'calc(100vw - 16px)', sm: 380 },
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: { xs: 'calc(100vh - 96px)', sm: 'min(600px, calc(100vh - 120px))' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
            '& .MuiTypography-root, & .MuiButton-root': { fontFamily: brandConfig.fontFamily },
          },
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827', lineHeight: 1.3 }}>
            Notifications
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            {unreadCount > 0
              ? `${unreadCount > 99 ? '99+' : unreadCount} non lue${unreadCount > 1 ? 's' : ''}`
              : 'Aucune non lue'}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={markAllAsRead}
          disabled={!hasUnread}
          startIcon={<DoneAllIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: primary,
            borderRadius: '10px',
            '&:hover': { backgroundColor: `${primary}0f` },
          }}
        >
          Tout marquer comme lu
        </Button>
        <IconButton
          size="small"
          aria-label="Fermer les notifications"
          onClick={onClose}
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: '#6B7280' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <NotificationList notifications={notifications} onNavigate={onClose} />
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
      <Button
        fullWidth
        onClick={handleSeeAll}
        endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
        sx={{
          flexShrink: 0,
          py: 1.25,
          borderRadius: 0,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.85rem',
          color: primary,
          '&:hover': { backgroundColor: `${primary}0f` },
        }}
      >
        Voir toutes les notifications
      </Button>
    </Popover>
  );
};

export default NotificationPanel;
